import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button, Progress, Spin } from 'antd'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { Preppy } from '../Preppy'
import { useAssistant } from '../AssistantProvider'
import { GUIDES } from '../mockData'
import { anchorSelector, useAnchorRects, useSpotlight, type SpotlightRect } from './useSpotlight'

/**
 * The flying walkthrough: Preppy undocks from the panel, flies to the real
 * element on the real screen, spotlights it and points.
 *
 * Help happens on the actual UI rather than in a screenshot, which is the whole
 * argument for having walkthroughs in the product at all — anything that does
 * not need to point at something is a Help Centre article instead.
 */

const FLYER_SIZE = 58
const BUBBLE_WIDTH = 300
const EDGE = 14
const GAP = 20
/** Clear of the app header, which the dim covers but does not move. */
const TOP_SAFE = 80
/** Distance between the flyer and its bubble — must match the CSS offset below. */
const TAIL_GAP = 15

type Viewport = { width: number; height: number }

type Placement = {
  left: number
  top: number
  side: 'left' | 'right'
}

/** Horizontal span the flyer and its bubble occupy together. */
function footprint(left: number, side: 'left' | 'right') {
  const bubble =
    side === 'right'
      ? { from: left + FLYER_SIZE + TAIL_GAP, to: left + FLYER_SIZE + TAIL_GAP + BUBBLE_WIDTH }
      : { from: left - TAIL_GAP - BUBBLE_WIDTH, to: left - TAIL_GAP }
  return { from: Math.min(left, bubble.from), to: Math.max(left + FLYER_SIZE, bubble.to) }
}

const overlapWidth = (a: { from: number; to: number }, b: { from: number; to: number }) =>
  Math.max(0, Math.min(a.to, b.to) - Math.max(a.from, b.from))

/**
 * Put Preppy in empty space, not on top of the thing being explained.
 *
 * Sitting beside the anchor works while anchors are small. It falls apart on a
 * step that points at a whole form — `step-body` on Agenda and Data is most of
 * the screen — where "beside" has nowhere to go and Preppy ends up parked over
 * the fields the user is being asked to fill in. On a coached walkthrough that
 * is the one thing it must never do.
 *
 * So candidates are scored by how much they cover the anchor, and the clearest
 * one wins: beside it if there is room, otherwise the page margin, which on a
 * centred `max-w-6xl` layout is exactly the whitespace this is looking for.
 */
function placeFlyer(
  rect: SpotlightRect | null,
  bubbleHeight: number,
  viewport: Viewport
): Placement {
  const viewportW = viewport.width

  if (!rect) {
    return {
      left: Math.max(EDGE, viewportW - FLYER_SIZE - EDGE),
      top: flyerTop(null, bubbleHeight, viewport),
      side: 'left',
    }
  }

  const anchorSpan = { from: rect.left, to: rect.left + rect.width }

  const candidates: Array<{ left: number; side: 'left' | 'right' }> = [
    // Beside the anchor, which is right for anything small.
    { left: rect.left + rect.width + GAP, side: 'right' },
    { left: rect.left - GAP - FLYER_SIZE, side: 'left' },
    // Failing that, the page margins — the whitespace either side of a centred
    // content column.
    { left: viewportW - EDGE - FLYER_SIZE, side: 'left' },
    { left: EDGE, side: 'right' },
  ]

  const scored = candidates
    .map((candidate) => {
      const span = footprint(candidate.left, candidate.side)
      return {
        ...candidate,
        fits: span.from >= EDGE && span.to <= viewportW - EDGE,
        covers: overlapWidth(span, anchorSpan),
      }
    })
    // Anything that would hang off the screen is not a candidate at all.
    .filter((candidate) => candidate.fits)
    .sort((a, b) => a.covers - b.covers)

  // Nothing fits (a very narrow window): fall back to the right edge and let it
  // clamp, which is at least where Preppy normally lives.
  const best = scored[0] ?? {
    left: viewportW - EDGE - FLYER_SIZE,
    side: 'left' as const,
    covers: Number.POSITIVE_INFINITY,
  }

  const left = Math.max(EDGE, Math.min(viewportW - FLYER_SIZE - EDGE, best.left))

  const below = best.covers > 0 ? belowAnchorTop(rect, bubbleHeight, viewport) : null

  return { left, top: below ?? flyerTop(rect, bubbleHeight, viewport), side: best.side }
}

/** Underneath the anchor, if the whole bubble fits there. Null if it does not. */
function belowAnchorTop(
  rect: SpotlightRect,
  bubbleHeight: number,
  viewport: Viewport
): number | null {
  const half = Math.max(bubbleHeight, FLYER_SIZE) / 2
  const centre = rect.top + rect.height + GAP + half
  if (centre + half > viewport.height - EDGE) return null
  return centre - FLYER_SIZE / 2
}

/** Where Preppy sits vertically. The bubble hangs off its middle, so its height decides. */
function flyerTop(
  rect: SpotlightRect | null,
  bubbleHeight: number,
  viewport: Viewport
): number {
  const viewportH = viewport.height
  const desired = rect ? rect.top + rect.height / 2 : viewportH / 2
  const half = bubbleHeight / 2

  const minCentre = Math.max(TOP_SAFE + FLYER_SIZE / 2, EDGE + half)
  const maxCentre = Math.min(viewportH - EDGE - FLYER_SIZE / 2, viewportH - EDGE - half)

  const centre =
    maxCentre < minCentre ? viewportH / 2 : Math.max(minCentre, Math.min(maxCentre, desired))

  return centre - FLYER_SIZE / 2
}

export function WalkthroughOverlay() {
  const { state, actions } = useAssistant()
  const { walk, walkProgress, walkWaiting } = state

  const guide = walk ? GUIDES.find((g) => g.id === walk.guideId) ?? null : null
  const stepIndex = walk?.index ?? 0
  const step = guide?.steps[stepIndex] ?? null
  // Counted among the steps this user can actually run — skipped ones would
  // otherwise make the numbering lie.
  const shownIndex = walkProgress?.current ?? 1
  const shownTotal = walkProgress?.total ?? guide?.steps.length ?? 0
  // Derived in the provider from what this user can still reach, so it stays
  // right if an analysis finishing mid-walk unlocks a step that was skipped.
  const isLast = walkProgress?.isLast ?? false

  const [viewport, setViewport] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }))
  useEffect(() => {
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight })
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const { rect, status, missing, onModal } = useSpotlight(step?.anchor ?? null, step?.focusAnchor)

  const allRects = useAnchorRects(step?.anchor ?? null, Boolean(step?.highlightAll) && !onModal)
  const multiRects = allRects.length > 1 && !missing ? allRects : null
  const nearestRect = useMemo(() => {
    if (!multiRects) return null
    const middle = viewport.height / 2
    const distance = (r: SpotlightRect) => Math.abs(r.top + r.height / 2 - middle)
    return multiRects.reduce((best, r) => (distance(r) < distance(best) ? r : best))
  }, [multiRects, viewport.height])

  const nextButtonRef = useRef<HTMLButtonElement>(null)
  const bubbleRef = useRef<HTMLDivElement>(null)
  const holeMaskId = useId()

  // Measured before paint: a ResizeObserver alone reports after it, and the
  // flyer transitions `top`, so the correction would be visible.
  const [bubbleHeight, setBubbleHeight] = useState(0)
  const measureBubble = useCallback(() => {
    const element = bubbleRef.current
    if (element) setBubbleHeight(element.offsetHeight)
  }, [])

  useLayoutEffect(measureBubble, [measureBubble, onModal, status, stepIndex, walk, walkWaiting])

  useEffect(() => {
    const element = bubbleRef.current
    if (!element) return
    const observer = new ResizeObserver(measureBubble)
    observer.observe(element)
    return () => observer.disconnect()
  }, [measureBubble, walk])

  const placement = useMemo(
    () => placeFlyer(missing ? null : nearestRect ?? rect, bubbleHeight, viewport),
    [bubbleHeight, missing, nearestRect, rect, viewport]
  )

  /**
   * Watch for a click on the spotlit element (`advanceWhen: 'anchorClicked'`).
   *
   * Delegated rather than bound: the element can be replaced by a re-render,
   * and the click usually lands on something inside it — an antd Button renders
   * a `<span>` for its label. Capture phase, so a handler that stops
   * propagation cannot swallow the signal.
   */
  const watchedAnchor =
    step?.advanceWhen === 'anchorClicked' ? step.clickAnchor ?? step.anchor : null
  const notifyAnchorClicked = actions.notifyAnchorClicked
  useEffect(() => {
    if (!watchedAnchor) return
    const selector = anchorSelector(watchedAnchor)
    const onClick = (event: MouseEvent) => {
      const target = event.target
      if (target instanceof Element && target.closest(selector)) notifyAnchorClicked()
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [notifyAnchorClicked, watchedAnchor])

  // Escape stops the walk, same as the Stop button.
  useEffect(() => {
    if (!walk) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        actions.endWalk(false)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [actions, walk])

  // Move focus onto each new step so the walkthrough is keyboard-drivable. A
  // disabled button cannot take focus, so this must match the button's own
  // condition exactly or focus lands on the body.
  useEffect(() => {
    if (!walk) return
    if (
      step?.choices?.length ||
      (walkWaiting && status !== 'missing' && !step?.waitOptional)
    ) {
      bubbleRef.current?.focus()
    } else {
      nextButtonRef.current?.focus()
    }
  }, [status, step?.choices?.length, step?.waitOptional, stepIndex, walk, walkWaiting])

  if (!walk || !guide || !step) return null

  const bubbleSideStyle =
    placement.side === 'right'
      ? { left: FLYER_SIZE + TAIL_GAP }
      : { right: FLYER_SIZE + TAIL_GAP }

  const dimClass = step.allowInteraction ? 'preppy-dim preppy-dim--pass' : 'preppy-dim'

  return createPortal(
    <FeatureThemeProvider featureKey="advisoryIntel" withAntd>
      {/* Spotlight. The dim blocks clicks everywhere except the hole, so the
          highlighted element is the only thing the user can act on — which is
          why an opening dialog has to become the spotlight (see useSpotlight). */}
      {multiRects ? (
        <>
          {!step.allowInteraction && <div className="preppy-dim" style={{ inset: 0, background: 'transparent' }} />}
          <svg className="preppy-dim-sheet" aria-hidden>
            <defs>
              <mask id={holeMaskId}>
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                {multiRects.map((r, index) => (
                  <rect
                    key={index}
                    x={r.left - 6}
                    y={r.top - 6}
                    width={r.width + 12}
                    height={r.height + 12}
                    rx="10"
                    fill="black"
                  />
                ))}
              </mask>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="rgba(17, 24, 39, 0.4)"
              mask={`url(#${holeMaskId})`}
            />
          </svg>
          {multiRects.map((r, index) => (
            <div
              key={index}
              className="preppy-spotlight"
              style={{ left: r.left - 6, top: r.top - 6, width: r.width + 12, height: r.height + 12 }}
            />
          ))}
        </>
      ) : rect && !missing ? (
        (() => {
          // Four panes framing the hole. Each blocks; the hole does not exist as
          // an element at all, so clicks land on the app underneath.
          const hole = {
            left: Math.max(0, rect.left - 6),
            top: Math.max(0, rect.top - 6),
            right: rect.left + rect.width + 6,
            bottom: rect.top + rect.height + 6,
          }
          return (
            <>
              <div className={dimClass} style={{ inset: `0 0 auto 0`, height: hole.top }} />
              <div
                className={dimClass}
                style={{ top: hole.top, left: 0, width: hole.left, height: hole.bottom - hole.top }}
              />
              <div
                className={dimClass}
                style={{ top: hole.top, left: hole.right, right: 0, height: hole.bottom - hole.top }}
              />
              <div className={dimClass} style={{ top: hole.bottom, left: 0, right: 0, bottom: 0 }} />
              <div
                className="preppy-spotlight"
                style={{
                  left: hole.left,
                  top: hole.top,
                  width: hole.right - hole.left,
                  height: hole.bottom - hole.top,
                }}
              />
            </>
          )
        })()
      ) : (
        // Nothing to point at yet — dim everything, and block it too, so a
        // half-started step cannot be clicked through.
        <div className={dimClass} style={{ inset: 0 }} />
      )}

      <div
        className="preppy-flyer"
        style={{ left: placement.left, top: placement.top, width: FLYER_SIZE, height: FLYER_SIZE }}
      >
        <div className="preppy-bob">
          <Preppy mood="point" facing={placement.side} size={FLYER_SIZE} />
        </div>

        <div
          ref={bubbleRef}
          role="dialog"
          tabIndex={-1}
          aria-label={`${guide.title}, step ${shownIndex} of ${shownTotal}`}
          className="absolute top-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl"
          style={{
            width: BUBBLE_WIDTH,
            maxHeight: viewport.height - EDGE * 2,
            ...bubbleSideStyle,
          }}
        >
          <div
            className="text-[9.5px] font-extrabold uppercase tracking-[0.1em]"
            style={{ color: 'var(--feature-primary-strong)' }}
          >
            Walkthrough
          </div>

          {/* Three states, because "still loading" and "not there" deserve very
              different things said about them. Telling someone to skip a step
              that was about to work is worse than saying nothing. */}
          <p className="mt-1.5 text-[13px] leading-relaxed text-gray-800" aria-live="polite">
            {status === 'searching' && 'One moment — still loading that part of the screen.'}
            {status === 'missing' &&
              'That part of the screen is not showing right now — skip ahead and I will pick up from the next step.'}
            {status === 'found' &&
              (onModal
                ? step.modalText ?? step.text
                : walkWaiting
                  ? step.waitText ?? step.text
                  : (walk.sample && step.sampleText) || step.text)}
          </p>

          {walkWaiting && status === 'found' && !step.choices?.length && (
            <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-400">
              <Spin size="small" />
              <span>
                {step.advanceWhen === 'analysisReady'
                  ? 'Waiting for the analysis'
                  : 'Waiting for you'}
              </span>
            </div>
          )}

          <Progress
            percent={shownTotal ? (shownIndex / shownTotal) * 100 : 0}
            showInfo={false}
            size="small"
            strokeColor="var(--feature-primary)"
            className="mt-3 mb-0"
          />
          <div className="text-[10.5px] text-gray-400">
            Step {shownIndex} of {shownTotal} · {guide.title}
          </div>

          {step.choices?.length ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {step.choices.map((choice) => (
                <Button
                  key={choice.goTo}
                  size="small"
                  type={choice.secondary ? 'default' : 'primary'}
                  className={choice.secondary ? undefined : 'feature-button-solid'}
                  onClick={() => actions.walkChoose(choice)}
                >
                  {choice.label}
                </Button>
              ))}
              {shownIndex > 1 && (
                <Button size="small" onClick={() => actions.walkTo(stepIndex - 1)}>
                  Back
                </Button>
              )}
              <Button type="text" size="small" className="text-gray-400" onClick={() => actions.endWalk(false)}>
                Stop
              </Button>
            </div>
          ) : (
          <div className="mt-3 flex items-center gap-2">
            {/* Disabled rather than hidden while waiting. There IS a next step;
                you just have not done this one yet, and a vanished button reads
                as broken. No skip: on a coached flow, skipping the work is the
                one thing that would make the rest of it meaningless. Stop is
                always there, so nobody is trapped. */}
            <Button
              ref={nextButtonRef}
              type="primary"
              size="small"
              className="feature-button-solid"
              /**
               * Enabled only once we have given up looking. `missing` needs the
               * escape hatch the copy above promises; `searching` must not have
               * it, or a slow page load hands the user a live Next on the step
               * that is waiting for their click. `waitOptional` steps keep it
               * live throughout — there the click is a shortcut, not the point.
               */
              disabled={walkWaiting && status !== 'missing' && !step.waitOptional}
              onClick={() => (isLast ? actions.endWalk(true) : actions.walkTo(stepIndex + 1))}
            >
              {isLast ? 'Done' : step.nextLabel ?? 'Next'}
            </Button>
            {shownIndex > 1 && (
              <Button size="small" onClick={() => actions.walkTo(stepIndex - 1)}>
                Back
              </Button>
            )}
            <Button type="text" size="small" className="text-gray-400" onClick={() => actions.endWalk(false)}>
              Stop
            </Button>
          </div>
          )}
        </div>
      </div>
    </FeatureThemeProvider>,
    document.body
  )
}
